import React, {
  useState,
  useContext,
  createContext,
  useCallback,
  useMemo,
} from "react";
import { Socket } from "socket.io-client";
import { useSocket } from "./socket.context";
import { EVENT_NAMES, trackAppEvent } from "@/shared/utils/events/trackEvent";

export type FlowStatus = "uninitialized" | "loading" | "idle" | "error";

export interface Message {
  type: "message";
  role: "system" | "user" | "assistant";
  content: string;
}

export interface ToolCall {
  type: "function_call";
  call_id: string;
  name: string;
  arguments: string;
}

export interface ToolCallResponse {
  type: "function_call_output";
  call_id: string;
  output: string;
}

export type ContextItem = Message | ToolCall | ToolCallResponse;

export interface LLMEvent {
  event:
    | "on_llm_start" // marks the start of when the LLM starts generating a response
    | "recursive_call" // sent before sending tool call outputs to the LLM for response generation
    | "new_output_block" // sent only for a streaming response, marks the start of a new (incomplete) output block
    | "output_delta" // sent only for a streaming response, contains a single token from an output block
    | "full_output_block" // a complete output block, sent both in streaming and non-streaming mode
    | "on_llm_end" // marks the end of a LLM response generation run, contains usage stats
    | "tool_call" // a full tool call (we don't stream arguments separately)
    | "tool_call_result"; // a block containing the result for a previous tool call
  item?: ContextItem;
  delta?: string;
  refusal?: boolean;
  usage?: {
    input_tokens: number;
    output_tokens: number;
    [key: string]: any;
  };
}

export interface ChatMessage {
  role:
    | "system"
    | "user"
    | "assistant"
    | "tool_call"
    | "tool_call_response"
    | "tool";
  content: string | ToolCall | ToolCallResponse | null;
  tool_calls?: ToolCall[];
  tool_call_id?: string;
}

export interface FlowEvent {
  conversationId: string;
  nodeId: string;
  type:
    | "nodeProcessStart"
    | "nodeOutput"
    | "nodeProcessEnd"
    | "chatEvent"
    | "nodeStatusUpdate";
  outputName?: string; // TODO - temporary type
}

export type NodePortDataType =
  | "string"
  | "chatMessages"
  | "chatMessage"
  | "LLMEvent"
  | "contextBlock"
  | "object";

export interface NodeOutputEvent<T> extends FlowEvent {
  type: "nodeOutput";
  outputName: string;
  dataType: NodePortDataType;
  content: T;
}

export interface NodeStatusUpdateEvent<T> extends FlowEvent {
  type: "nodeStatusUpdate";
  content: T;
}

export interface ChatEvent extends FlowEvent {
  type: "chatEvent";
  chatEvent: LLMEvent;
}

export interface SetupFlowResponse {
  conversationId: string;
  toggleableNodes: string[];
  runnableNodes: string[];
}

export interface UserPromptParams {
  conversationId: string;
  prompt: string;
  params?: Record<string, any>;
}

export interface RunFlowRequest {
  conversationId: string;
  runNodeId: string;
  toggleNodes: Record<string, boolean>;
}

export interface RunFlowParams {
  conversationId?: string;
  runNodeId: string;
  toggleNodes?: Record<string, boolean>;
  requestParams?: Record<string, any>;
}

export interface GptContextType {
  flowStatus: FlowStatus; // @deprecated - use getFlowStatus instead
  getFlowStatus: (conversationId?: string) => FlowStatus;
  error: Error | undefined;
  initializedFlowsMap: Record<string, SetupFlowResponse> | undefined;
  socket: Socket | null;
  initializeFlow: (
    flowId: string,
    signedScope: string
  ) => Promise<SetupFlowResponse | undefined>;
  sendUserPrompt: (params: UserPromptParams) => Promise<void>;
  runFlow: (params: RunFlowParams) => Promise<void>;
}

interface GptProviderProps {
  children: React.ReactNode;
}

export const GptContext = createContext<GptContextType>({
  flowStatus: "uninitialized",
  getFlowStatus: () => "uninitialized",
  error: undefined,
  initializedFlowsMap: undefined,
  socket: null,
  initializeFlow: async () => undefined,
  sendUserPrompt: async () => {},
  runFlow: async () => {},
});

function GptProvider({ children }: GptProviderProps) {
  const [flowStatus, setFlowStatus] = useState<FlowStatus>("uninitialized"); // @deprecated
  const [flowStatusMap, setFlowStatusMap] = useState<
    Record<string, FlowStatus>
  >({});
  const [initializedFlowsMap, setInitializedFlowsMap] =
    useState<Record<string, SetupFlowResponse>>();
  const [error, setError] = useState<Error | undefined>(undefined);

  const { socket } = useSocket();

  const getFlowStatus = (conversationId?: string) => {
    if (!conversationId) return flowStatus; // Fallback to global status
    return flowStatusMap[conversationId] || "uninitialized";
  };

  const initializeFlow = async (flowId: string, signedScope: string) => {
    setFlowStatus("loading"); // Global fallback
    if (!socket || !flowId || !signedScope) {
      setFlowStatus("error");
      setError(new Error("Socket or flowId or signedScope is not set"));
      return;
    }
    try {
      const res = await socket.timeout(5000).emitWithAck("SetupFlow", {
        flowId,
        signedScope,
      });
      if (res.type === "error") {
        console.error(res);
        setError(res);
        setFlowStatus("error");
      } else {
        setInitializedFlowsMap((prev) => ({
          ...prev,
          [res.conversationId]: res,
        }));
        setFlowStatusMap((prev) => ({
          ...prev,
          [res.conversationId]: "idle",
        }));
        setFlowStatus("idle");
      }
      return res;
    } catch (err) {
      setFlowStatus("error");
      setError(
        err instanceof Error ? err : new Error("Unknown error occurred")
      );
    }
  };

  const sendUserPrompt = useCallback(
    async (params: UserPromptParams) => {
      setFlowStatus("loading"); // Global fallback
      setFlowStatusMap((prev) => ({
        ...prev,
        [params.conversationId]: "loading",
      }));

      const error = await socket?.emitWithAck("UserPrompt", {
        conversationId: params.conversationId,
        prompt: params.prompt,
        requestParams: params.params,
      });

      if (error && error.type === "error") {
        console.error(error);
        setError(error);
        setFlowStatus("error");
        setFlowStatusMap((prev) => ({
          ...prev,
          [params.conversationId]: "error",
        }));
      } else {
        setFlowStatus("idle");
        setFlowStatusMap((prev) => ({
          ...prev,
          [params.conversationId]: "idle",
        }));
      }
    },
    [socket]
  );

  const runFlow = useCallback(
    async (params: RunFlowParams) => {
      const conversationId = params.conversationId;
      setFlowStatus("loading"); // Global fallback

      if (conversationId) {
        setFlowStatusMap((prev) => ({
          ...prev,
          [conversationId]: "loading",
        }));
      }

      const error = await socket?.emitWithAck("RunFlow", {
        conversationId: params.conversationId,
        ...params,
      } as RunFlowRequest);

      if (error && error.type === "error") {
        console.error(error);
        setError(error);
        setFlowStatus("error");
        if (conversationId) {
          setFlowStatusMap((prev) => ({
            ...prev,
            [conversationId]: "error",
          }));
        }
      } else {
        setFlowStatus("idle");
        if (conversationId) {
          setFlowStatusMap((prev) => ({
            ...prev,
            [conversationId]: "idle",
          }));
        }
      }
    },
    [socket]
  );

  const contextValue = useMemo(
    () => ({
      flowStatus,
      getFlowStatus,
      error,
      initializedFlowsMap,
      socket,
      initializeFlow,
      sendUserPrompt,
      runFlow,
    }),
    [
      flowStatus,
      getFlowStatus,
      error,
      initializedFlowsMap,
      socket,
      initializeFlow,
      sendUserPrompt,
      runFlow,
    ]
  );

  return (
    <GptContext.Provider value={contextValue}>{children}</GptContext.Provider>
  );
}

export const useGpt = () => useContext(GptContext);

export default GptProvider;
